import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FaDownload } from 'react-icons/fa';
import { BiRefresh } from 'react-icons/bi';
import { useCloudflareAuth } from '../../../contexts/CloudflareAuthContext';
import api from '../../../services/api';
import { captureFixedDesign } from '../../../utils/captureFixedDesign';
import certificateBackground from '../../../assets/Certificate-ccro.png';

interface UserDetails {
  fullName: string;
  enrollmentNumber: string;
  memberType: string;
  designation: string;
  branch?: string;
  phone: string;
  currentState: string;
  currentDistrict: string;
  currentCityVillage: string;
  currentPinCode: string;
  dateOfBirth: string;
  bloodGroup: string;
  validUntil: string;
  memberStatus?: string;
  photoURL?: string;
  fatherOrHusbandName?: string;
}

// The certificate is a fixed 794x1123 (A4) design — scaled visually on narrow
// screens; downloads still capture full resolution (ref on the un-scaled element).
const CERT_W = 794;
const CERT_H = 1123;

const ScaleCertToWidth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / CERT_W));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="w-full" style={{ height: CERT_H * scale }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: CERT_W, height: CERT_H }}>
        {children}
      </div>
    </div>
  );
};

const CertificateDownload: React.FC = () => {
  const { currentUser, userDetails: authUserDetails } = useCloudflareAuth();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const certificateRef = useRef<HTMLDivElement>(null);

  const fetchMemberDetails = useCallback(async (userId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getUser(userId);
      const data = response.details || response.user;

      if (!data) {
        setError('No member details found');
        return;
      }

      // Get photo URL from API
      const photoURL = data.photo || api.getFileUrl(userId, 'photo');

      setUserDetails({
        fullName: data.full_name || data.fullName || '',
        enrollmentNumber: data.enrollment_number || data.enrollmentNumber || '',
        memberType: data.member_type || data.memberType || '',
        designation: data.designation || '',
        branch: data.branch,
        phone: data.phone || '',
        currentState: data.current_state || data.currentState || '',
        currentDistrict: data.current_district || data.currentDistrict || '',
        currentCityVillage: data.current_city_village || data.currentCityVillage || '',
        currentPinCode: data.current_pin_code || data.currentPinCode || '',
        dateOfBirth: data.date_of_birth || data.dateOfBirth || '',
        bloodGroup: data.blood_group || data.bloodGroup || '',
        validUntil: data.valid_until || data.validUntil || '',
        photoURL,
        fatherOrHusbandName: data.father_or_husband_name || data.fatherOrHusbandName,
        memberStatus: data.member_status || data.memberStatus || ''
      });
    } catch (err) {
      setError('Error fetching member details');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchMemberDetails(currentUser.uid);
    } else {
      setError('Please login to view your certificate');
      setIsLoading(false);
    }
  }, [currentUser, fetchMemberDetails]);

  const capitalizeWords = (str: string) => {
    if (!str) return '';
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const downloadCertificate = async () => {
    if (!certificateRef.current) return;

    const canvas = await captureFixedDesign(certificateRef.current, CERT_W, CERT_H);

    canvas.toBlob(blob => {
      if (blob) {
        const link = document.createElement('a');
        const formattedDate = userDetails?.validUntil?.replace(/-/g, '') || 'certificate';
        link.href = URL.createObjectURL(blob);
        link.download = `CCRO_Certificate_${userDetails?.enrollmentNumber || ''}_${formattedDate}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    }, 'image/png');
  };

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'No member details found'}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            <BiRefresh className="mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isActiveMember = userDetails.memberStatus?.toLowerCase() === 'active';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="p-6 space-y-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-400 uppercase tracking-[0.3em]">Download</p>
          <h1 className="text-3xl font-bold text-white">Membership Certificate</h1>
        </div>
        <div>
          <button
            onClick={downloadCertificate}
            disabled={!isActiveMember}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50"
          >
            <FaDownload />
            Download Certificate
          </button>
        </div>
      </div>

      {!isActiveMember && (
        <div className="p-4 rounded-lg border border-yellow-500/40 bg-yellow-500/10 text-yellow-200">
          Certificate access is limited to active members. Please contact support if you believe this is an error.
        </div>
      )}

      <div className="flex justify-center items-center md:min-h-[800px]">
        <div className="flex flex-col items-center gap-4 w-full max-w-full">
          <ScaleCertToWidth>
          <div
            ref={certificateRef}
            className="relative w-[794px] h-[1123px] bg-white rounded-[32px] shadow-[0_25px_50px_rgba(0,0,0,0.35)] overflow-hidden"
            style={{ opacity: isActiveMember ? 1 : 0.35 }}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${certificateBackground})` }}
            />

            <div className="relative h-full z-10 pointer-events-none">
              {userDetails.photoURL && (
                <div
                  className="absolute flex justify-center"
                  style={{ top: '450px', left: '15.5%' }}
                >
                  <div className="w-[130px] h-[160px] rounded-2xl overflow-hidden border-4 border-white shadow-[0_15px_35px_rgba(0,0,0,0.35)] bg-white/80">
                    <img
                      src={userDetails.photoURL}
                      alt={userDetails.fullName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}

              <div
                className="absolute text-left"
                style={{ top: '490px', left: '36%', fontFamily: 'Bahnschrift' }}
              >
                <p className="text-2xl font-black text-white uppercase drop-shadow">
                  {capitalizeWords(userDetails.fullName)}
                </p>
                <p className="text-lg font-semibold text-white leading-tight drop-shadow-sm">
                  {capitalizeWords(userDetails.currentCityVillage)}, {capitalizeWords(userDetails.currentDistrict)}
                </p>
                <p className="text-lg font-semibold text-white leading-tight drop-shadow-sm">
                  {capitalizeWords(userDetails.currentState)} - {userDetails.currentPinCode}
                </p>
              </div>

              <div className="absolute top-[700px] left-[270px] right-[140px] flex flex-col text-left" style={{ fontFamily: 'Bahnschrift' }}>
                <p className="text-base font-semibold text-black drop-shadow flex items-center gap-2">
                  <span>{capitalizeWords(userDetails.memberType || '')}</span>
                  <span>{capitalizeWords(userDetails.designation || 'Member')},</span>
                  <span>Branch: {capitalizeWords(userDetails.branch || 'Branch')}</span>
                </p>
              </div>
            </div>
          </div>
          </ScaleCertToWidth>

          <p className="text-sm text-slate-400">
            Certificate mirror design from Admin panel (A4) for print-ready downloads.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default CertificateDownload;
